import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  PRICE_BREAKDOWN_LINE_ITEMS,
  PriceBreakdownData,
  getPriceBreakdownLineColor,
} from '../utils/priceBreakdown'
import { fmtCurrency } from '../utils/propertyFormat'

interface PriceBreakdownModalProps {
  open: boolean
  title: string
  breakdown: PriceBreakdownData | null
  onClose: () => void
}

function formatLineValue(value: number, currency: string): string {
  if (value === 0) return '—'
  return fmtCurrency(value, currency)
}

export default function PriceBreakdownModal({
  open,
  title,
  breakdown,
  onClose,
}: PriceBreakdownModalProps) {
  const theme = useTheme()

  if (!breakdown) return null

  const currency = breakdown.currency || 'ILS'
  const totalAfterFees = breakdown.totalAfterFees || breakdown.totalBeforeFees
  const lineValues = PRICE_BREAKDOWN_LINE_ITEMS.map((item) => ({
    ...item,
    value: breakdown[item.key] as number,
    color: getPriceBreakdownLineColor(
      item.category,
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.mode
    ),
  }))
  const maxLineValue = Math.max(...lineValues.map((item) => item.value), 1)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            <ReceiptLongIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Price breakdown
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            bgcolor: (t) =>
              t.palette.mode === 'light'
                ? `${t.palette.primary.main}14`
                : `${t.palette.primary.main}28`,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Total after fees
          </Typography>
          <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mt: 0.5 }}>
            {fmtCurrency(totalAfterFees, currency)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {lineValues.map((item) => {
            const hasValue = item.value > 0
            const proportion = hasValue ? item.value / maxLineValue : 0
            return (
              <Box key={item.key} sx={{ opacity: hasValue ? 1 : 0.5 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: hasValue ? item.color : 'text.disabled',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" noWrap title={item.label}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={hasValue ? 600 : 400}
                    color={hasValue ? 'text.primary' : 'text.disabled'}
                  >
                    {formatLineValue(item.value, currency)}
                  </Typography>
                </Box>
                {hasValue && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(proportion * 100, 3)}
                    sx={{
                      mt: 0.75,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        bgcolor: item.color,
                        opacity: theme.palette.mode === 'light' ? 0.65 : 0.85,
                      },
                    }}
                  />
                )}
              </Box>
            )
          })}
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total before fees
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatLineValue(breakdown.totalBeforeFees, currency)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: 'action.selected',
            }}
          >
            <Typography variant="subtitle2">Total after fees</Typography>
            <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
              {fmtCurrency(totalAfterFees, currency)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
